"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { API_CONFIG, COOKIE_CONFIG } from "@/config";
import { getCookie, setCookie } from "cookies-next";

interface BuildVersion {
  version: string;
}

const fetchBuildVersion = async (
  signal?: AbortSignal,
): Promise<BuildVersion> => {
  try {
    const response = await fetch("/api/check-build", {
      cache: "no-store",
      signal,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Version check failed:", error);
    throw error;
  }
};

const POSTPONE_DELAY = API_CONFIG.postponeDelay; // 1 minute in milliseconds
const POLLING_INTERVAL = API_CONFIG.pollingInterval; // 1 minutes in milliseconds
const ENABLE_POLLING = API_CONFIG.enablePolling; // Flag to enable/disable polling

export const useVersionCheck = () => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const checkAndUpdateVersion = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await fetchBuildVersion(signal);
      const currentVersion = getCookie(COOKIE_CONFIG.version);

      if (!currentVersion) {
        setCookie(COOKIE_CONFIG.version, data.version);
        return false;
      }

      return data.version !== currentVersion;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        console.error("Version check failed:", error);
      }
      return false;
    }
  }, []);

  // Route change and beforeunload check
  useEffect(() => {
    const handleVersionCheck = async () => {
      const needsUpdate = await checkAndUpdateVersion();
      if (needsUpdate) {
        setCookie(COOKIE_CONFIG.version, (await fetchBuildVersion()).version);
        window.location.reload();
      }
    };

    // Initial check
    handleVersionCheck();

    // Create a void function wrapper for beforeunload
    const handleBeforeUnload = () => {
      void handleVersionCheck();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [checkAndUpdateVersion]);

  // Internal polling effect
  useEffect(() => {
    if (!ENABLE_POLLING) return;

    const pollVersion = async () => {
      const needsUpdate = await checkAndUpdateVersion();
      if (needsUpdate) setShowUpdatePrompt(true);
    };

    // Initial poll
    pollVersion();

    // Set up interval
    pollingIntervalRef.current = setInterval(pollVersion, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [checkAndUpdateVersion]);

  const handleUpdate = useCallback(async () => {
    try {
      const data = await fetchBuildVersion();
      setCookie(COOKIE_CONFIG.version, data.version);
      window.location.reload();
    } catch (error) {
      console.error("Update failed:", error);
    }
  }, []);

  const postponeUpdate = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowUpdatePrompt(false);
    timeoutRef.current = setTimeout(
      () => setShowUpdatePrompt(true),
      POSTPONE_DELAY,
    );
  }, []);

  // Cleanup timeout and polling interval
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    },
    [],
  );

  return { showUpdatePrompt, handleUpdate, postponeUpdate };
};
